import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, TextInput, ActivityIndicator, Keyboard } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { storage } from '../../utils/storage';
import { submitReview } from '../../lib/api/reviews';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import ThemedText from '../../components/common/ThemedText';

type SubmitReviewNavigationProp = StackNavigationProp<RootStackParamList, 'SubmitReview'>;
type SubmitReviewRouteProp = RouteProp<RootStackParamList, 'SubmitReview'>;

interface RatingCategory {
  key: string;
  label: string;
  value: number;
}

const SubmitReviewScreen: React.FC = () => {
  const navigation = useNavigation<SubmitReviewNavigationProp>();
  const route = useRoute<SubmitReviewRouteProp>();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const { userId, userName } = route.params;

  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const [ratings, setRatings] = useState<RatingCategory[]>([
    { key: 'overallRating', label: 'Overall Rating', value: 1 },
    { key: 'paymentsRating', label: 'Payments', value: 1 },
    { key: 'serviceRating', label: 'Service', value: 1 },
    { key: 'qualityRating', label: 'Quality', value: 1 },
    { key: 'workingRating', label: 'Working', value: 1 },
    { key: 'communicationRating', label: 'Communication', value: 1 },
    { key: 'partialityRating', label: 'Partiality', value: 1 },
    { key: 'opportunitiesRating', label: 'Opportunities', value: 1 },
  ]);

  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
      setKeyboardVisible(true);
    });
    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  const updateRating = (index: number, value: number) => {
    const newRatings = [...ratings];
    newRatings[index].value = value;
    setRatings(newRatings);
  };

  const handleSubmit = async () => {
    // Validate all ratings
    console.log('Current ratings:', ratings);
    const missingRatings = ratings.filter(r => r.value < 1 || r.value > 5);
    console.log('Missing/invalid ratings:', missingRatings);
    if (missingRatings.length > 0) {
      showErrorToast('Please provide valid ratings for all categories (1-5 stars)');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await storage.getToken();
      if (!token) {
        showErrorToast('Please log in to submit a review');
        return;
      }

      const reviewData = {
        overall: ratings[0].value,
        payments: ratings[1].value,
        service: ratings[2].value,
        quality: ratings[3].value,
        working: ratings[4].value,
        communication: ratings[5].value,
        partiality: ratings[6].value,
        opportunities: ratings[7].value,
      };
      
      console.log('Submitting review data:', reviewData);
      console.log('Review text:', reviewText.trim() || undefined);
      console.log('User ID:', userId);

      const result = await submitReview(
        token,
        userId,
        reviewData,
        reviewText.trim() || undefined
      );
      
      console.log('Review submission result:', result);

      showSuccessToast('Review submitted successfully!');
      // Navigate back with refresh flag
      navigation.navigate('Profile', { 
        userId: userId,
        refresh: true 
      });
    } catch (error: any) {
      console.error('Review submission error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      showErrorToast(error.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating: number, onPress: (star: number) => void) => {
    return (
      <View style={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <TouchableOpacity
            key={star}
            onPress={() => onPress(star)}
            style={styles.starButton}
          >
            <Ionicons
              name={star <= rating ? 'star' : 'star-outline'}
              size={32}
              color={star <= rating ? '#FFB800' : theme.textTertiary}
            />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          Rate & Review
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView 
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.businessInfo, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
          <Ionicons name="business" size={24} color={theme.primary} />
          <ThemedText style={[styles.businessName, { color: theme.text }]}>
            {userName || 'Business'}
          </ThemedText>
        </View>

        <View style={styles.ratingsSection}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
            Rate the following aspects
          </ThemedText>
          <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>
            Tap stars to rate from 1 to 5
          </ThemedText>

          {ratings.map((rating, index) => (
            <View key={rating.key} style={[styles.ratingRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.ratingLabel, { color: theme.text }]}>
                {rating.label}
              </ThemedText>
              {renderStars(rating.value, (star) => updateRating(index, star))}
            </View>
          ))}
        </View>

        <View style={styles.reviewSection}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
            Write a Review (Optional)
          </ThemedText>
          <TextInput
            style={[
              styles.reviewInput,
              { 
                backgroundColor: theme.backgroundSecondary, 
                color: theme.text,
                borderColor: theme.border,
              }
            ]}
            placeholder="Share your experience working with this business..."
            placeholderTextColor={theme.textSecondary}
            value={reviewText}
            onChangeText={setReviewText}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
          />
        </View>

        {/* Keyboard spacer */}
        {keyboardVisible && <View style={{ height: 300 }} />}
      </ScrollView>

      <View style={[styles.footer, { 
        backgroundColor: theme.background, 
        borderTopColor: theme.border,
        paddingBottom: insets.bottom + 16
      }]}>
        <TouchableOpacity
          style={[styles.submitButton, { backgroundColor: theme.accent }]}
          onPress={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <ActivityIndicator color={theme.textInverse} />
          ) : (
            <>
              <Ionicons name="checkmark-circle" size={20} color={theme.textInverse} />
              <ThemedText style={[styles.submitButtonText, { color: theme.textInverse }]}>
                Submit Review
              </ThemedText>
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },
  businessInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    marginBottom: 24,
  },
  businessName: {
    fontSize: 18,
    fontWeight: '600',
  },
  ratingsSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  ratingRow: {
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  ratingLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  starButton: {
    padding: 4,
  },
  reviewSection: {
    marginBottom: 24,
  },
  reviewInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    minHeight: 120,
    borderWidth: 1,
    marginTop: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    gap: 8,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});

export default SubmitReviewScreen;
